import React, { useState, useEffect } from 'react';
import './PrecatoriosDesignados.css';
import { getToken } from '../../../../services/api';
import { useNavigate } from 'react-router-dom';

const PrecatoriosDesignados = () => {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('alphabetical');
  const [filterText, setFilterText] = useState('');
  const navigate = useNavigate();

  // All Brazilian states in alphabetical order
  const allStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  // State names for better filtering
  const stateNames = {
    'AC': 'Acre',
    'AL': 'Alagoas',
    'AP': 'Amapá',
    'AM': 'Amazonas',
    'BA': 'Bahia',
    'CE': 'Ceará',
    'DF': 'Distrito Federal',
    'ES': 'Espírito Santo',
    'GO': 'Goiás',
    'MA': 'Maranhão',
    'MT': 'Mato Grosso',
    'MS': 'Mato Grosso do Sul',
    'MG': 'Minas Gerais',
    'PA': 'Pará',
    'PB': 'Paraíba',
    'PR': 'Paraná',
    'PE': 'Pernambuco',
    'PI': 'Piauí',
    'RJ': 'Rio de Janeiro',
    'RN': 'Rio Grande do Norte',
    'RS': 'Rio Grande do Sul',
    'RO': 'Rondônia',
    'RR': 'Roraima',
    'SC': 'Santa Catarina',
    'SP': 'São Paulo',
    'SE': 'Sergipe',
    'TO': 'Tocantins'
  };

  useEffect(() => {
    fetchGroupedCounts();
  }, []);

  const fetchGroupedCounts = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      const response = await fetch(`${API_URL}/precatorio-group-status/comercial/grouped-counts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar contagem de grupos');
      }

      const data = await response.json();
      
      // Initialize all states with 0 count, then override with actual data
      const allCounts = {};
      allStates.forEach(state => {
        allCounts[state] = 0;
      });
      
      // Override with actual counts from backend
      Object.assign(allCounts, data);
      
      setCounts(allCounts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStateClick = (estado) => {
    if (counts[estado] > 0) {
      navigate(`/comercial/precatorios-designados/${estado}`);
    }
  };

  // Filter and sort states
  const getFilteredAndSortedStates = () => {
    let filteredStates = allStates.filter(state => {
      const searchLower = filterText.toLowerCase();
      return state.toLowerCase().includes(searchLower) || 
             stateNames[state].toLowerCase().includes(searchLower);
    });

    switch (sortBy) {
      case 'alphabetical':
        break;
      case 'count-desc':
        filteredStates.sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
        break;
      case 'count-asc':
        filteredStates.sort((a, b) => (counts[a] || 0) - (counts[b] || 0));
        break;
      default:
        break;
    }

    return filteredStates;
  };

  // Calculate total groups
  const totalGroups = Object.values(counts).reduce((sum, count) => sum + count, 0);

  if (loading) {
    return (
      <div className="precatorios-designados-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="precatorios-designados-container">
        <div className="error">Erro: {error}</div>
      </div>
    );
  }

  const displayStates = getFilteredAndSortedStates();

  return (
    <div className="precatorios-designados-container">
      <h1>Precatórios Designados</h1>
      <p className="subtitle">
        Você tem <strong>{totalGroups}</strong> grupos de CPF/CNPJ designados para gerenciar. 
        Clique em um estado para ver os detalhes.
      </p>
      
      <div className="filter-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por sigla ou nome do estado..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="sort-controls">
          <label htmlFor="sort-select">Ordenar por:</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="alphabetical">Alfabética (A-Z)</option>
            <option value="count-desc">Mais grupos primeiro</option>
            <option value="count-asc">Menos grupos primeiro</option>
          </select>
        </div>
      </div>

      <div className="states-info">
        <p>Exibindo {displayStates.length} de {allStates.length} estados</p>
      </div>
      
      <div className="states-grid">
        {displayStates.map((estado) => (
          <div 
            key={estado} 
            className={`state-card ${counts[estado] === 0 ? 'empty' : ''}`}
            onClick={() => handleStateClick(estado)}
            title={stateNames[estado]}
          >
            <h2>{estado}</h2>
            <div className="count">{counts[estado] || 0}</div>
            <p className="label">Grupos de CPF/CNPJ</p>
            <p className="state-name">{stateNames[estado]}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrecatoriosDesignados;
